import { ALLOWED_LINKS_AS_TABS, AnalyticsEventTopic, getOafIdFromEvent } from '../../analytics/analyticsHelper';
import { AnalyticsEvent, DataCloudEventData } from '../../services/analyticsService';
import { AnalyticsWeightings } from '../analyticsWeightings';
import {
    DATA_CLOUD_APP_INSTALL_TYPE,
    DATA_CLOUD_CASE_STUDY_TYPE,
    DATA_CLOUD_DATA_SHEET_TYPE,
    DATA_CLOUD_TESTIMONIAL_TYPE,
    DATA_CLOUD_UNINSTALL_TYPE,
    DATA_CLOUD_WEBINAR_TYPE,
    DATA_CLOUD_WHITE_PAPER_TYPE,
    DataCloudEventSource,
    DataCloudEventTypeValues,
} from './constants';
import { DataCloudEventType, DataCloudIdentityData } from './datacloudEventType';

export interface CustomDataCloudAnalyticsEvent {
    interaction: {
        name: string;
        anonymous?: string;
        username?: string;
        clientId?: string;
        contentIdKey?: string;
        contentIdValue?: string;
        listingName?: string;
        listingCategoryName?: string;
        customEventType?: string;
        customEventValue?: string;
        eventType: string;
        language?: string;
        outcome?: number;
        source?: string;
        userAgent?: string;
        deviceId: string;
        location?: string;
    };
    user: {
        attributes: DataCloudIdentityData & {
            eventType: string;
            deviceId: string;
        };
    };
}
export interface CustomSpecificBodyType {
    eventType?: string;
    outcome?: number;
}

abstract class CustomDataCloudEventType extends DataCloudEventType {
    protected abstract customSpecificBody: CustomSpecificBodyType;

    abstract canHandle(event: AnalyticsEvent): boolean;

    getEventName(): string {
        return this.customSpecificBody.eventType || 'customEvent';
    }
    getBody(event: DataCloudEventData): CustomDataCloudAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const listingCategories = this.getListingCategories(event.listing?.listingCategories);
        const oafId = getOafIdFromEvent(event);

        return {
            interaction: {
                name: 'customEvent',
                anonymous: baseEvent.anonymous ? '1' : '0',
                username: baseEvent.username,
                clientId: baseEvent.clientId,
                contentIdKey: 'permanentId',
                contentIdValue: oafId,
                listingName: event.listing?.title || event.title,
                listingCategoryName:
                    (listingCategories?.length && listingCategories[0]) || event.listingCategoryName || '',
                customEventType: this.customSpecificBody.eventType,
                customEventValue: oafId,
                eventType: DataCloudEventTypeValues.customEvent,
                language: baseEvent.language,
                outcome: baseEvent.outcome,
                source: DataCloudEventSource.site,
                userAgent: baseEvent.userAgent,
                deviceId: baseEvent.username,
                location: window.location.href,
            },
            user: {
                attributes: {
                    ...baseEvent.userData,
                    eventType: DataCloudEventTypeValues.identity,
                    deviceId: baseEvent.username,
                },
            },
        };
    }
}

export class TileViewDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_LOAD_IMPRESSION && event.elementType === 'listingTile';
    }
    customSpecificBody = { eventType: 'tileView', outcome: AnalyticsWeightings.defaultPoints };
}

export class TileClickDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && event.elementType === 'listingTile';
    }
    customSpecificBody = { eventType: 'tileClick', outcome: AnalyticsWeightings.defaultPoints };
}
export class PageViewDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_PAGE_LOAD &&
            event.dataCloud?.eventAction === 'appxListingDetail'
        );
    }
    customSpecificBody = { eventType: 'pageView', outcome: AnalyticsWeightings.pageViewPoints };
}
export class ScreenshotDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CAROUSEL_SHOWN_IMPRESSION;
    }
    customSpecificBody = { eventType: 'screenshot', outcome: AnalyticsWeightings.defaultPoints };
}

export class ListingTabSwitchDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
            (event.elementType === 'tab' ||
                (event.elementType === 'link' && ALLOWED_LINKS_AS_TABS.includes(event.link?.callToAction?.name || '')))
        );
    }
    customSpecificBody = { eventType: 'listingTabSwitch', outcome: AnalyticsWeightings.defaultPoints };
}

const COMPETENCIES_ROW_TOGGLE_NAMES = [
    'competencies_industry_row_expand',
    'competencies_industry_row_collapse',
    'competencies_product_row_expand',
    'competencies_product_row_collapse',
];

export class CompetenciesRowToggleDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
            COMPETENCIES_ROW_TOGGLE_NAMES.includes(event.button?.callToAction?.name || '')
        );
    }
    customSpecificBody = { eventType: 'competenciesRowToggle', outcome: AnalyticsWeightings.defaultPoints };
}

export class UninstallDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_LIKE_REVIEW;
    }
    customSpecificBody = { eventType: DATA_CLOUD_UNINSTALL_TYPE };
}

export class CaseStudyDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && event.customTitle?.toLowerCase() === 'case study';
    }
    customSpecificBody = { eventType: DATA_CLOUD_CASE_STUDY_TYPE };
}

export class DataSheetDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && event.customTitle?.toLowerCase() === 'data sheet';
    }
    customSpecificBody = { eventType: DATA_CLOUD_DATA_SHEET_TYPE, outcome: AnalyticsWeightings.dataSheetPoints };
}

export class WebinarDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && event.customTitle?.toLowerCase() === 'webinar';
    }
    customSpecificBody = { eventType: DATA_CLOUD_WEBINAR_TYPE, outcome: AnalyticsWeightings.webinarPoints };
}

export class TestimonialDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
            event.customTitle?.toLowerCase() === 'customer testimonial'
        );
    }
    customSpecificBody = {
        eventType: DATA_CLOUD_TESTIMONIAL_TYPE,
        outcome: AnalyticsWeightings.customerTestimonialPoints,
    };
}

export class AppInstallDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_USER_INSTALL;
    }
    customSpecificBody = { eventType: DATA_CLOUD_APP_INSTALL_TYPE };
}

export class WhitePaperDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && event.customTitle?.toLowerCase() === 'whitepaper';
    }
    customSpecificBody = { eventType: DATA_CLOUD_WHITE_PAPER_TYPE, outcome: AnalyticsWeightings.whitePaperPoints };
}

export class TestDriveDataCloudEventType extends CustomDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return event.topic === '_testDrive_';
    }
    customSpecificBody = { eventType: 'testDrive' };
}
